/* eslint-disable no-unused-vars */

import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Container,
  Divider,
  FormControl,
  Grid,
  InputAdornment,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import { Page } from 'src/components';
import { LoaderBar } from 'src/components';
import { cloneDeep, debounce } from 'lodash';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';
import { Autocomplete, Pagination } from '@material-ui/lab';
import { FieldArray, Formik, FormikProps } from 'formik';
import { IconButton } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';

import useUserInfo from 'src/hooks/user/use-user-info';
import { formatCurrency, formatDate } from 'src/utils';
import { colors } from 'src/constants';
import {
  CreatePurchaseOrderPayload,
  PurchaseOrderItem
} from 'src/redux/slices/purchase-order/types';
import { unwrapResult } from '@reduxjs/toolkit';
import { Supplier } from 'src/types/supplier';
import { DatePicker } from '@material-ui/pickers';
import {
  CommonProductsResponse,
  Product,
  ProductsThunkRequest
} from 'src/types';
import { LinkComponent } from 'src/components';
import {
  allowedStatusToEditTerms,
  ForTaxCertificateOptions,
  initialEmptyProduct,
  initialPurchaseOrderValues,
  PurchaseOrderStatusesV2
} from 'src/redux/slices/purchase-order';
import { useSnackBar } from 'src/hooks';
import PurchaseOrderStatusSelect from './PurchaseOrderStatusSelect';
import PurchaseOrderStatusField from './PurchaseOrderStatusSelect';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import CloseIcon from '@material-ui/icons/Close';
import useResolution from 'src/hooks/useResolution';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { AlternateRowContainer } from 'src/views/product/component/AlternateRowContainer';
import { TextRow } from 'src/views/product/component/TextRow';
import CameraAltIcon from '@material-ui/icons/CameraAlt';
import html2canvas from 'html2canvas';
import { getVat2307Value, getVatIncValue } from 'src/utils/vatUtils';

const {
  actions: ecomOrderActions,
  selectors: ecomOrderSelectors
} = slices.ecomOrder;

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3)
  },
  productCard: {
    height: '100%'
  },
  columnHeader: {
    border: '1px solid #7d879c'
  },
  tableCell: {
    borderBottom: '1px solid #7d879c'
  },
  mainDetails: {
    display: 'flex',
    rowGap: '.1em',
    flexDirection: 'column',
    padding: '1em'
  }
}));

const { actions: productActions } = slices.product;
const {
  actions: supplierActions,
  selectors: supplierSelectors
} = slices.supplier;

interface PurchaseOrderFormProps {
  formikRef?: any;
  isEditable?: boolean;
  isForDelivery?: boolean;
  currentStatusValue?: string;
  originalSINo?: string | null;
}

export const component = ({
  formikRef,
  isEditable,
  isForDelivery,
  originalSINo,
  currentStatusValue
}: PurchaseOrderFormProps) => {
  const formikRefAsInternal: FormikProps<CreatePurchaseOrderPayload> = formikRef;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const classes = useStyles();
  const snackbar = useSnackBar();
  const dispatch = useAppDispatch();
  const { userBranchOptions } = useUserInfo();
  const { isMobile } = useResolution();

  const [focusedIndex, setFocusedIndex] = React.useState<number | null>(null);

  const disableUpdateSiNo = useMemo(
    () =>
      formikRefAsInternal?.values?.status !==
        PurchaseOrderStatusesV2.FOR_PRODUCT_IN &&
      formikRefAsInternal?.values?.status !==
        PurchaseOrderStatusesV2?.FOR_DELIVERY,
    [formikRefAsInternal.values.status]
  );

  const [supplierOptions, setSupplierOptions] = useState<Supplier[]>([]);
  const [termsValue, setTermsValue] = useState<string>('');
  const [productOptions, setProductOptions] = useState<Product[]>([]);
  const [isProductsLoading, setIsProductsLoading] = useState<boolean>(false);
  const [productSearchInput, setProductSearchInput] = useState('');
  const siPercentage = useMemo(
    () => Number(formikRefAsInternal?.values?.si_percentage) || 0,
    [formikRefAsInternal]
  );

  const getSuppliers = async () => {
    const response = unwrapResult(
      await dispatch(
        supplierActions.getSuppliersThunk({
          keyword: '',
          limit: 9999,
          module: 'purchase-order'
        })
      )
    );
    if (
      response?.success &&
      response?.originalData?.data &&
      response?.originalData?.data?.length > 0
    ) {
      setSupplierOptions(response?.originalData?.data);
    }
  };

  const getProducts = useMemo(
    () =>
      debounce(async (keyword: string) => {
        if (keyword?.length < 2) return;

        setIsProductsLoading(true);
        try {
          const response = unwrapResult(
            await dispatch(
              productActions.getProductsThunk({
                keyword,
                purpose: 'purchase_order'
              })
            )
          );

          if (response.success && response.originalData?.data) {
            setProductOptions(response.originalData.data || []);
          } else {
            setProductOptions([]);
          }
        } finally {
          setIsProductsLoading(false);
        }
      }, 1000),
    [dispatch]
  );

  const productTotal = useMemo(() => {
    const products: PurchaseOrderItem[] =
      formikRefAsInternal?.values?.products || [];

    const total = products.reduce((acc: number, product: PurchaseOrderItem) => {
      return acc + (product.dealer_price || 0) * (product.quantity || 0);
    }, 0);
    return total;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formikRefAsInternal?.values?.products]);

  const selectedBranch = useMemo(() => {
    const chosenBranch = userBranchOptions?.find(
      (b) => b.value === formikRefAsInternal?.values.branch_id
    );
    return chosenBranch;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userBranchOptions, formikRefAsInternal?.values?.branch_id]);

  const selectedSupplier = useMemo(() => {
    const chosenSupplier = supplierOptions?.find(
      (b) => b.id === formikRefAsInternal?.values.supplier_id
    );
    return chosenSupplier;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierOptions, formikRefAsInternal?.values?.supplier_id]);

  const selectedForTaxCertificate = useMemo(() => {
    const chosenOption = ForTaxCertificateOptions?.find(
      (b) =>
        b.value ===
        (formikRefAsInternal?.values.for_tax_certificate ?? 'for 2307')
    );
    return chosenOption;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formikRefAsInternal?.values.for_tax_certificate]);

  const previousSupplierId = useRef<number | null>(null);

  useEffect(() => {
    const currentSupplierId = formikRefAsInternal?.values?.supplier_id;

    if (selectedSupplier && previousSupplierId.current !== currentSupplierId) {
      formikRefAsInternal?.setFieldValue('terms', selectedSupplier.terms ?? '');
    }

    previousSupplierId.current = currentSupplierId ?? null;
  }, [selectedSupplier, formikRefAsInternal]);

  const onAddProductRow = () => {
    const clonedProducts = cloneDeep(formikRefAsInternal.values.products);
    clonedProducts?.push(initialEmptyProduct);

    formikRefAsInternal.setFieldValue('products', clonedProducts);
  };

  const onDeleteProductRow = (indexToRemove: number) => {
    const updated = formikRefAsInternal?.values?.products?.filter(
      (_, i) => i !== indexToRemove
    );

    formikRefAsInternal.setFieldValue('products', updated);
  };

  const onSelectProduct = useCallback(
    (productArg: Product | null) => {
      if (!productArg) {
        return;
      }

      // const clonedProducts = cloneDeep(formikRefAsInternal.values.products);
      // clonedProducts?.push(initialEmptyProduct);
      // formikRefAsInternal.setFieldValue('products', clonedProducts);

      // TODO: What if selected product already exist

      // Check if the product already exists in the list
      // const existingProductIndex = formikRefAsInternal?.values?.products?.findIndex(
      //   (product: PurchaseOrderItem) => product.product_id === productArg.id
      // );
      // if (existingProductIndex !== undefined && existingProductIndex > -1) {
      //   snackbar.show({
      //     message: 'Product already exists in the list',
      //     severity: 'warning',
      //     useSound: true
      //   });
      //   return;
      // }

      const newProductRow = {
        ...initialEmptyProduct,
        product_id: productArg?.id || null,
        product_name: productArg?.name || null,
        dealer_price: productArg?.dealers_price || 0,
        quantity: 1,
        sku: productArg?.sku || null,
        category: productArg?.category || null,
        original_dealer_price: productArg?.dealers_price || 0
      };

      formikRefAsInternal?.setValues((prevValues: any) => {
        const updatedProducts = [...prevValues.products, newProductRow];
        return {
          ...prevValues,
          products: updatedProducts
        };
      });
    },
    [formikRefAsInternal]
  );

  const isProductDuplicate = useCallback(
    (productId?: number | null) => {
      if (!productId) {
        return false;
      }
      const productsViaProductId = formikRefAsInternal?.values?.products?.filter(
        (product: PurchaseOrderItem) => product.product_id === productId
      );
      return productsViaProductId && productsViaProductId?.length > 1;
    },
    [formikRefAsInternal.values.products]
  );

  useEffect(() => {
    getSuppliers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMultipleProductsEnter = async (multiLineStr: string) => {
    const multiProductsArr = multiLineStr
      ?.split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    if (multiProductsArr?.length === 0) {
      snackbar.show({
        message: 'No products found in the input. Please check your input.',
        severity: 'warning'
      });
      return;
    }

    for (const productName of multiProductsArr) {
      const response = unwrapResult(
        await dispatch(
          productActions.getProductsThunk({
            keyword: productName,
            purpose: 'purchase_order'
          })
        )
      );

      if (response?.originalData?.data?.length === 1) {
        const newProductRow = response?.originalData?.data[0];
        onSelectProduct(newProductRow);
      } else {
        snackbar.show({
          message: `Product "${productName}" not found.`,
          severity: 'warning'
        });
      }
    }
  };

  const onScreenShot = async () => {
    const autocompleteComponent: any = document.querySelector(
      '#hide-autocomplete-product'
    );
    const productsTable: any = document.querySelector('#products-table');

    autocompleteComponent?.style.setProperty('display', 'none');

    const cloned = productsTable.cloneNode(true);
    cloned.style.width = 'auto';

    const wrapper = document.createElement('div');
    wrapper.style.padding = isMobile ? '0px' : '20px';
    wrapper.style.background = 'white';
    wrapper.style.display = 'inline-block';
    wrapper.style.position = 'absolute';
    wrapper.style.top = '-9999px';

    wrapper.appendChild(cloned);
    document.body.appendChild(wrapper);

    try {
      const canvas = await html2canvas(wrapper, {
        useCORS: true,
        allowTaint: true
      });

      canvas.toBlob(
        async (blob: any) => {
          try {
            // @ts-ignore
            navigator.clipboard
              // @ts-ignore
              // eslint-disable-next-line no-undef
              ?.write([new ClipboardItem({ 'image/png': blob })])
              ?.then(() => {
                snackbar.show({
                  severity: 'success',
                  message: 'Image copied to clipboard'
                });
              });
          } catch (err) {
            console.error('Clipboard error:', err);
          } finally {
            autocompleteComponent?.style.setProperty('display', 'block');
            document.body.removeChild(wrapper);
          }
        },
        'image/png',
        0.99
      );
    } catch (err) {
      console.error('Screenshot error:', err);
      autocompleteComponent?.style.setProperty('display', 'block');
      document.body.removeChild(wrapper);
    }
  };

  const unitCostFieldHelperCopy = useCallback((item?: PurchaseOrderItem) => {
    if (!item?.dealer_price || !item?.original_dealer_price) {
      return '';
    }
    if (item?.dealer_price === item?.original_dealer_price) {
      return '';
    }
    return `DP: ${formatCurrency(
      item?.original_dealer_price || 0
    )} | Diff: ${formatCurrency(
      (item?.original_dealer_price || 0) - (item?.dealer_price || 0) || 0
    )}`;
  }, []);

  const getTotalAmount = useCallback(
    (item) => (item?.dealer_price || 0) * (item?.quantity || 0),
    []
  );

  // parse dates into proper format api friendly format
  useEffect(() => {
    if (formikRef.values.po_id) {
      const { delivery_date, po_date } = formikRef.values;
      po_date
        ? formikRefAsInternal?.setFieldValue(
            'po_date',
            formatDate(po_date, 'yyyy-MM-dd'),
            true
          )
        : null;
      delivery_date
        ? formikRefAsInternal?.setFieldValue(
            'delivery_date',
            formatDate(delivery_date, 'yyyy-MM-dd'),
            true
          )
        : null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formikRef.values.po_id]);

  // reset si no field if edited while not on for product in status
  useEffect(() => {
    if (
      formikRefAsInternal?.values.si_no !== originalSINo &&
      formikRefAsInternal?.values.status !==
        PurchaseOrderStatusesV2.FOR_PRODUCT_IN &&
      formikRefAsInternal?.values.si_no?.length
    ) {
      formikRefAsInternal.setFieldValue('si_no', originalSINo ?? null);
    }
  }, [formikRefAsInternal, originalSINo]);

  useEffect(() => {
    const defaultValue =
      formikRefAsInternal?.values.for_tax_certificate || 'for 2307';
    formikRefAsInternal?.setFieldValue(
      'for_tax_certificate',
      defaultValue,
      true
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <form onSubmit={formikRefAsInternal?.handleSubmit}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <Typography variant="subtitle1">Branch</Typography>
          <Autocomplete
            disabled={!isEditable}
            id="branch_id"
            options={userBranchOptions}
            fullWidth
            getOptionSelected={(option, value) => option.value === value.value}
            getOptionLabel={(option) => option.name}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select Branch"
                variant="outlined"
                helperText={
                  formikRefAsInternal?.touched.branch_id &&
                  formikRefAsInternal?.errors.branch_id
                }
                error={Boolean(
                  formikRefAsInternal?.touched.branch_id &&
                    formikRefAsInternal?.errors.branch_id
                )}
              />
            )}
            value={selectedBranch || null}
            onBlur={formikRefAsInternal?.handleBlur}
            onChange={(_, val) => {
              formikRefAsInternal?.setFieldValue('branch_id', val?.value, true);
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Typography variant="subtitle1">Select Supplier</Typography>
          <Autocomplete
            id="supplier_id"
            options={supplierOptions}
            disabled={!isEditable}
            fullWidth
            getOptionLabel={(option) => option.name || ''}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Choose Supplier"
                variant="outlined"
                helperText={
                  formikRefAsInternal?.touched.supplier_id &&
                  'Supplier is required'
                }
                error={Boolean(
                  formikRefAsInternal?.touched.supplier_id &&
                    formikRefAsInternal?.errors.supplier_id
                )}
              />
            )}
            value={selectedSupplier || null}
            onBlur={formikRefAsInternal?.handleBlur}
            onChange={(_, val) => {
              formikRefAsInternal?.setFieldValue('supplier_id', val?.id, true);
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Typography variant="subtitle1">Delivery Date</Typography>
          <DatePicker
            fullWidth
            format="MMMM dd, yyyy"
            inputVariant="outlined"
            variant="inline"
            name="delivery_date"
            id="delivery_date"
            placeholder="- - -"
            autoOk
            value={formikRefAsInternal?.values.delivery_date}
            onChange={(value) => {
              formikRefAsInternal?.setFieldValue(
                'delivery_date',
                formatDate(value, 'yyyy-MM-dd'),
                true
              );
            }}
            onBlur={formikRefAsInternal?.handleBlur}
            helperText={
              formikRefAsInternal?.touched.delivery_date &&
              formikRefAsInternal?.errors.delivery_date
            }
            error={Boolean(
              formikRefAsInternal?.touched.delivery_date &&
                formikRefAsInternal?.errors.delivery_date
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle1">Remarks</Typography>
          <TextField
            fullWidth
            size="medium"
            variant="outlined"
            multiline
            minRows={2}
            value={formikRefAsInternal?.values?.remarks || ''}
            onBlur={formikRefAsInternal?.handleBlur}
            onChange={formikRefAsInternal?.handleChange}
            name={`remarks`}
            helperText={
              formikRefAsInternal?.touched.remarks &&
              formikRefAsInternal?.errors.remarks
            }
            error={Boolean(
              formikRefAsInternal?.touched.remarks &&
                formikRefAsInternal?.errors.remarks
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth variant="outlined">
            <Box style={{ display: 'flex', gap: '.2em', alignItems: 'center' }}>
              <Typography
                variant="subtitle1"
                style={{ fontWeight: '500', color: 'rgb(38, 50, 56)' }}
              >
                Internal Remarks
              </Typography>
              <Typography variant="subtitle1" style={{ color: '#888' }}>
                {'(For PO viewing only, not in printed form)'}
              </Typography>
            </Box>
            <TextField
              fullWidth
              size="medium"
              variant="outlined"
              multiline
              minRows={2}
              value={formikRefAsInternal?.values?.internal_remarks || ''}
              onBlur={formikRefAsInternal?.handleBlur}
              onChange={formikRefAsInternal?.handleChange}
              name={`internal_remarks`}
              helperText={
                formikRefAsInternal?.touched.remarks &&
                formikRefAsInternal?.errors.remarks
              }
              error={Boolean(
                formikRefAsInternal?.touched.remarks &&
                  formikRefAsInternal?.errors.remarks
              )}
            />
          </FormControl>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        {formikRefAsInternal?.values?.order_no && (
          <Grid item md={4} sm={4} xs={12}>
            <Typography variant="subtitle1">DR No.</Typography>
            <TextField
              fullWidth
              disabled={!isForDelivery}
              variant="outlined"
              value={formikRefAsInternal?.values?.dr_no}
              onBlur={formikRefAsInternal?.handleBlur}
              onChange={formikRefAsInternal?.handleChange}
              name="dr_no"
              helperText={
                formikRefAsInternal?.touched.dr_no &&
                formikRefAsInternal?.errors.dr_no
              }
              error={Boolean(
                formikRefAsInternal?.touched.dr_no &&
                  formikRefAsInternal?.errors.dr_no
              )}
            />
          </Grid>
        )}

        {formikRefAsInternal?.values?.order_no && siPercentage ? (
          <Grid item md={4} sm={4} xs={12}>
            <Typography variant="subtitle1">Sales Invoice No.</Typography>
            <Tooltip
              title={
                disableUpdateSiNo
                  ? 'Update SI no. only available if PO status is equal to for delivery or product in.'
                  : ''
              }
            >
              <TextField
                fullWidth
                disabled={disableUpdateSiNo}
                variant="outlined"
                value={formikRefAsInternal?.values?.si_no || ''}
                onBlur={formikRefAsInternal?.handleBlur}
                onChange={formikRefAsInternal?.handleChange}
                placeholder="Sales Invoice No."
                name="si_no"
                helperText={
                  formikRefAsInternal?.touched.si_no &&
                  formikRefAsInternal?.errors.si_no
                }
                error={Boolean(
                  formikRefAsInternal?.touched.si_no &&
                    formikRefAsInternal?.errors.si_no
                )}
              />
            </Tooltip>
          </Grid>
        ) : null}

        <Grid item md={4} sm={4} xs={12}>
          <Typography variant="subtitle1">SI Percentage</Typography>
          <TextField
            fullWidth
            type="number"
            variant="outlined"
            name="si_percentage"
            value={formikRefAsInternal?.values.si_percentage}
            onBlur={formikRefAsInternal?.handleBlur}
            onChange={formikRefAsInternal.handleChange}
            onKeyDown={(e) => {
              // filter out non numerical math symbols
              if (['e', 'E', '+', '-'].includes(e.key)) {
                e.preventDefault();
                return;
              }
            }}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>
            }}
          />
        </Grid>

        <Grid item md={4} sm={4} xs={12}>
          <Typography variant="subtitle1">Terms</Typography>
          <TextField
            fullWidth
            disabled={
              !allowedStatusToEditTerms.includes(currentStatusValue || '')
            }
            variant="outlined"
            name="terms"
            value={
              formikRefAsInternal?.values?.terms?.replace(/\s*days$/, '') || ''
            }
            onBlur={formikRefAsInternal?.handleBlur}
            onChange={(e) => {
              const rawValue = e.target.value;

              if (/^\d*$/.test(rawValue)) {
                formikRefAsInternal?.setFieldValue(
                  'terms',
                  rawValue ? `${rawValue} days` : ''
                );
              }
            }}
            helperText={
              formikRefAsInternal?.touched.terms &&
              formikRefAsInternal?.errors.terms
            }
            error={Boolean(
              formikRefAsInternal?.touched.terms &&
                formikRefAsInternal?.errors.terms
            )}
            InputProps={{
              endAdornment: <InputAdornment position="end">days</InputAdornment>
            }}
          />
        </Grid>

        <Grid item md={4} sm={4} xs={12}>
          <Typography variant="subtitle1">2307</Typography>
          <Autocomplete
            id="for_tax_certificate"
            options={ForTaxCertificateOptions}
            fullWidth
            getOptionLabel={(option) => option.label || ''}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Choose For 2307"
                variant="outlined"
                helperText={
                  formikRefAsInternal?.touched.for_tax_certificate &&
                  'For 2307 is required'
                }
                error={Boolean(
                  formikRefAsInternal?.touched.for_tax_certificate &&
                    formikRefAsInternal?.errors.for_tax_certificate
                )}
              />
            )}
            value={
              ForTaxCertificateOptions.find(
                (opt) =>
                  opt.value === formikRefAsInternal?.values?.for_tax_certificate
              ) || null
            }
            onBlur={formikRefAsInternal?.handleBlur}
            onChange={(_, val) => {
              formikRefAsInternal?.setFieldValue(
                'for_tax_certificate',
                val?.value,
                true
              );
            }}
          />
        </Grid>
      </Grid>

      <div style={{ marginTop: 20 }} />
      <Card>
        <CardHeader
          title="Products"
          action={
            (formikRefAsInternal?.values?.products?.length ?? 0) > 0 ? (
              <Button
                variant="contained"
                color="primary"
                onClick={() => onScreenShot()}
              >
                <CameraAltIcon style={{ marginRight: 5 }} /> Screenshot List
              </Button>
            ) : null
          }
        />

        <Divider />
        <CardContent style={{ paddingTop: 20, paddingBottom: 20 }}>
          <div id="products-table">
            <PerfectScrollbar>
              <Table style={{ minWidth: '1000px', width: '100%' }}>
                <TableHead>
                  <TableRow className={classes.columnHeader}>
                    <TableCell style={{ width: '8%' }}>Quantity</TableCell>
                    <TableCell style={{ width: '40%' }}>Item</TableCell>
                    <TableCell style={{ width: '12%' }}>Unit Cost</TableCell>
                    <TableCell style={{ width: '13%' }}>
                      {siPercentage ? 'Amount (VAT EX)' : 'Amount'}
                    </TableCell>
                    {siPercentage ? (
                      <>
                        <TableCell style={{ width: '13%' }}>
                          Amount (VAT INC)
                        </TableCell>
                        <TableCell
                          style={{
                            width: '13%'
                          }}
                        >
                          Amount (2307)
                        </TableCell>
                      </>
                    ) : null}
                    {isEditable ? (
                      <TableCell style={{ width: '1%' }}></TableCell>
                    ) : null}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formikRefAsInternal?.values?.products?.map(
                    (item: PurchaseOrderItem, index: number) => (
                      <TableRow
                        key={index}
                        style={{
                          background: index % 2 ? colors.grey[200] : 'white'
                        }}
                      >
                        <TableCell className={classes.tableCell}>
                          <TextField
                            fullWidth
                            label="Quantity"
                            size="medium"
                            type="number"
                            disabled={!isEditable}
                            value={item.quantity}
                            onBlur={formikRefAsInternal?.handleBlur}
                            onChange={formikRefAsInternal?.handleChange}
                            name={`products.${index}.quantity`}
                            InputProps={{
                              disableUnderline: true,
                              style: {
                                border: 'none'
                              }
                            }}
                            style={{
                              border: 'none',
                              background: 'transparent'
                            }}
                          />
                        </TableCell>

                        <TableCell
                          className={classes.tableCell}
                          style={{
                            color: item?.message ? colors.error : undefined
                          }}
                        >
                          <div>
                            <Typography variant="h5">
                              {isProductDuplicate(item?.product_id) ? (
                                <Tooltip
                                  title="Product already exists"
                                  placement="top"
                                  style={{ marginRight: 5 }}
                                >
                                  <ErrorOutlineIcon
                                    fontSize="small"
                                    color="disabled"
                                  />
                                </Tooltip>
                              ) : null}
                              <LinkComponent
                                openInNewTab
                                href={`/app/products/${item.product_id}`}
                                title={`${item?.product_name || '?????'}`}
                                font="Roboto, Helvetica, Arial, sans-serif "
                              />
                            </Typography>
                            <Typography variant="caption">
                              {`(${item.category?.name ??
                                item?.category}) (${item?.sku || 'SKU N/A'})`}
                            </Typography>
                            {item?.message ? (
                              <Typography
                                variant="caption"
                                style={{ color: colors.error }}
                              >
                                | Error: {item?.message}
                              </Typography>
                            ) : (
                              ''
                            )}
                          </div>
                        </TableCell>

                        <TableCell className={classes.tableCell}>
                          {focusedIndex !== index ? (
                            <Box
                              onClick={() =>
                                isEditable && setFocusedIndex(index)
                              }
                              style={{
                                cursor: isEditable ? 'text' : 'default',
                                display: 'flex',
                                alignItems: 'start',
                                flexDirection: 'column'
                              }}
                            >
                              <Typography>
                                ₱{' '}
                                {Number(item?.dealer_price || 0).toLocaleString(
                                  'en-PH'
                                )}
                              </Typography>
                              <small
                                style={{
                                  display: 'flex',
                                  flexDirection: 'row'
                                }}
                              >
                                {unitCostFieldHelperCopy(item)}
                              </small>
                            </Box>
                          ) : (
                            <TextField
                              autoFocus
                              type="number"
                              disabled={!isEditable}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    ₱
                                  </InputAdornment>
                                ),
                                disableUnderline: true
                              }}
                              fullWidth
                              size="medium"
                              value={item?.dealer_price}
                              name={`products.${index}.dealer_price`}
                              onChange={formikRefAsInternal?.handleChange}
                              onBlur={(e) => {
                                setFocusedIndex(null);
                                formikRefAsInternal?.handleBlur(e);
                              }}
                              style={{
                                border: 'none',
                                background: 'transparent'
                              }}
                              helperText={unitCostFieldHelperCopy(item)}
                            />
                          )}
                        </TableCell>

                        <TableCell className={classes.tableCell}>
                          <TextField
                            fullWidth
                            disabled
                            size="medium"
                            value={`${formatCurrency(getTotalAmount(item))}`}
                            InputProps={{
                              disableUnderline: true,
                              style: { border: 'none' }
                            }}
                            style={{
                              border: 'none',
                              background: 'transparent'
                            }}
                          />
                        </TableCell>

                        {siPercentage ? (
                          <>
                            <TableCell className={classes.tableCell}>
                              <TextField
                                fullWidth
                                disabled
                                size="medium"
                                value={`${formatCurrency(
                                  getVatIncValue(
                                    item.dealer_price || 0,
                                    siPercentage || 0
                                  ) * (item?.quantity ?? 1)
                                )}`}
                                InputProps={{
                                  disableUnderline: true,
                                  style: {
                                    border: 'none'
                                  }
                                }}
                                style={{
                                  border: 'none',
                                  background: 'transparent'
                                }}
                              />
                            </TableCell>

                            <TableCell className={classes.tableCell}>
                              <TextField
                                fullWidth
                                disabled
                                size="medium"
                                value={`${formatCurrency(
                                  getVat2307Value(
                                    item.dealer_price || 0,
                                    siPercentage || 0
                                  ) * (item?.quantity ?? 1)
                                )}`}
                                InputProps={{
                                  disableUnderline: true,
                                  style: {
                                    border: 'none'
                                  }
                                }}
                                style={{
                                  border: 'none',
                                  background: 'transparent'
                                }}
                              />
                            </TableCell>
                          </>
                        ) : null}

                        <TableCell className={classes.tableCell}>
                          {/* <Button 
                            disabled={!isEditable} 
                            onClick={() => onDeleteProductRow(index)} 
                            color="secondary" 
                            variant="text" 
                          > 
                            <CloseIcon /> 
                          </Button> */}
                          {isEditable ? (
                            <CloseIcon
                              onClick={() => onDeleteProductRow(index)}
                              color="secondary"
                            />
                          ) : null}
                        </TableCell>
                      </TableRow>
                    )
                  )}
                </TableBody>
              </Table>
            </PerfectScrollbar>

            <div style={{ marginTop: 30 }} id="hide-autocomplete-product">
              <Typography variant="caption">
                Search Product and click to add to the list above.
              </Typography>
              <Autocomplete
                fullWidth
                disabled={!isEditable}
                loading={isProductsLoading}
                style={{ marginTop: 4 }}
                onChange={(_, val) => {
                  onSelectProduct(val);
                  setProductSearchInput('');
                }}
                onInputChange={(e, newInputValue, reason) => {
                  if (reason === 'reset') {
                    return;
                  }
                  setProductSearchInput(newInputValue);
                  getProducts(newInputValue);
                }}
                inputValue={productSearchInput}
                id="products"
                options={productOptions}
                getOptionLabel={(option) => option.name || ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    multiline={false}
                    inputRef={inputRef}
                    name="products_field"
                    placeholder="Search / Select / Add Product"
                    variant="outlined"
                  />
                )}
              />

              <Divider style={{ marginTop: 20 }} />

              <TextField
                style={{ marginTop: 20 }}
                fullWidth
                label="Enter Multiple Products"
                size="medium"
                variant="outlined"
                multiline
                minRows={2}
                disabled={!isEditable}
                onKeyDown={(e: any) => {
                  if (e.key === 'Enter') {
                    e.preventDefault(); // Prevent default behavior
                    handleMultipleProductsEnter(e.target.value);
                  }
                }}
              />
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'flex-end',
                paddingTop: 50,
                paddingRight: 20
              }}
            >
              <div
                style={{
                  display: 'flex',
                  borderBottom: '3px solid #7D879C',
                  alignItems: 'baseline'
                }}
              >
                <Typography
                  variant="h4"
                  style={{
                    paddingRight: 16,
                    fontSize: '1.5rem'
                  }}
                >
                  TOTAL:
                </Typography>
                <Typography
                  variant="h4"
                  style={{
                    fontWeight: 'bold',
                    fontSize: '1.5rem'
                  }}
                >
                  {siPercentage
                    ? formatCurrency(getVatIncValue(productTotal, siPercentage))
                    : formatCurrency(productTotal)}
                </Typography>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </form>
  );
};

export const PurchaseOrderForm = React.memo(component);
